import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Database, Cloud, CheckCircle2, XCircle, Loader2 } from "lucide-react";

/**
 * StorageHealthPills — Kontrol Paneli için MongoDB Atlas + Cloudflare R2
 * canlı sağlık göstergesi. 60 sn'de bir yenilenir.
 *
 * Görsel:
 *   [🗄 Atlas · 35.905 belge · 27ms]   [☁ R2 · 85 obje · 85 MB · 45ms]
 */
export default function StorageHealthPills() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/system/storage-health");
      setData(r);
    } catch {
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, []);

  const fmtBytes = (n) => {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };
  const fmtNum = (n) => (n == null ? "—" : n.toLocaleString("tr-TR"));

  if (busy) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="storage-health-loading">
        <Loader2 size={12} className="animate-spin"/> Sağlayıcı durumu okunuyor…
      </div>
    );
  }
  if (!data) return null;

  const mongo = data.mongo || {};
  const r2 = data.r2 || {};

  const Pill = ({ ok, icon: Icon, name, primary, secondary, testid, hue }) => (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
        ok
          ? `bg-${hue}-50 border-${hue}-200 text-${hue}-800 dark:bg-${hue}-900/30 dark:border-${hue}-500/40 dark:text-${hue}-200`
          : "bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-900/30 dark:border-rose-500/40 dark:text-rose-200"
      }`}
      data-testid={testid}
      title={ok ? `${name} — bağlı ve senkron` : `${name} — bağlantı hatası`}
    >
      <Icon size={12} className={ok ? `text-${hue}-600` : "text-rose-600"}/>
      {ok
        ? <CheckCircle2 size={11} className={`text-${hue}-600`}/>
        : <XCircle size={11} className="text-rose-600"/>}
      <span className="font-bold">{name}</span>
      <span className="tabular-nums">{primary}</span>
      <span className="text-slate-500 dark:text-slate-400">· {secondary}</span>
    </div>
  );

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="storage-health-pills">
      <Pill
        ok={mongo.ok}
        icon={Database}
        name={mongo.provider || "MongoDB"}
        primary={mongo.ok ? `${fmtNum(mongo.documents)} belge` : (mongo.error || "hata")}
        secondary={mongo.ok ? `${mongo.collections} koleksiyon · ${fmtBytes(mongo.data_bytes)} · ${mongo.latency_ms}ms` : "—"}
        testid="pill-mongo"
        hue="emerald"
      />
      <Pill
        ok={r2.ok}
        icon={Cloud}
        name={r2.provider || "Cloudflare R2"}
        primary={r2.ok ? `${fmtNum(r2.objects)} obje` : (r2.error || "hata")}
        secondary={r2.ok ? `${fmtBytes(r2.size_bytes)} · ${r2.latency_ms}ms` : "—"}
        testid="pill-r2"
        hue="sky"
      />
    </div>
  );
}
